<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('grocery_list')"
      :subtitle="$t('manage_ingredients')"
      icon="mdi-food-apple"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button
          v-if="$store.state.restaurant.parent"
          class="mr-2"
          @click="cloneGroceryDialog = true"
        >
          {{ $t("clone") }}
          {{ $store.state.restaurant.parent ? $store.state.restaurant.parent.name : "" }}
        </primary-button>

        <global-button-print @click="printGroceries" />
        <global-button-excel @click="exportGroceries" />

        <primary-button @click="editGroceryDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_grocery") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="searchGrocery"
      :search-placeholder="$t('search_ingredients_placeholder')"
      searchable
      :loading="isLoading"
    />

    <grocery-dialog
      v-model="editGroceryDialog"
      :edited-item="editedGrocery"
      @groceryCreated="addGrocery"
      @groceryUpdated="updateGrocery"
    />

    <global-dialog-card
      v-model="cloneGroceryDialog"
      :title="$t('clone')"
      :subtitle="$store.state.restaurant.parent?.name || ''"
    >
      <text-field
        v-model="searchParentGrocery"
        class="mt-2"
        append-icon="mdi-magnify"
        :label="$t('search')"
        hide-details
      ></text-field>
      <v-data-table
        dense
        :headers="headersN"
        class="ordera-box-shadow striped-table"
        :search="searchParentGrocery"
        style="height: 100%"
        :items="parentGroceries"
      >
        <template
          v-for="header in headersN"
          v-slot:[`header.${header.value}`]="{ header }"
        >
          <span v-if="header.mark">
            <input
              id="checkbox_id"
              type="checkbox"
              v-model="selectAllGroceries"
              @change.prevent="selectAll()"
            />
            <label for="checkbox_id">{{ $t("mark") }}</label>
          </span>
          <span v-else>{{ $t(header.text) }}</span>
        </template>
        <template v-slot:item.actions="{ item, index }">
          <span v-if="!prepData[item.id]">
            <input type="checkbox" v-model="item.selected" />
          </span>
          <span v-else style="font-size: 13px; color: rgba(0, 0, 0, 0.4)">{{
            $t("cloned")
          }}</span>
        </template>
      </v-data-table>
      <template #actions>
        <v-btn text @click="cloneGroceryDialog = false">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button @click="saveClone">
          {{ $t("clone") }}
        </primary-button>
      </template>
    </global-dialog-card>

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="customSearch(groceries, headers, searchGrocery)"
          class="striped-table ordera-box-shadow"
          :loading="isLoading"
          :loading-text="$t('loading_data')"
          show-expand
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <template v-if="item.children && item.children.length">
              <circle-button
                @click="expand(!isExpanded)"
                :icon="isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up'"
              ></circle-button>
            </template>
          </template>
          <template v-slot:expanded-item="{ item, isExpanded, expand }">
            <template v-if="item.children">
              <td :colspan="headers.length" class="px-0">
                <v-data-table
                  dense
                  :headers="headers"
                  class="ordera-box-shadow striped-table"
                  :items="item.children || []"
                  hide-default-footer
                  hide-default-header
                >
                  <template v-slot:item.actions="{ item, index }">
                    <edit-button class="mr-2" @click="editItem(item)"></edit-button>
                    <remove-button @click="deleteItem(item)"></remove-button>
                  </template>
                </v-data-table>
              </td>
            </template>
          </template>
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editItem(item)"></edit-button>
            <remove-button @click="deleteItem(item)"></remove-button>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-4">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="customSearch(groceries, headers, searchGrocery)"
          :loading="isLoading"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="4"
          :bold-fields="['name']"
        >
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editItem(item)"></edit-button>
            <remove-button @click="deleteItem(item)"></remove-button>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn color="primary" block @click="editGroceryDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_grocery") }}
        </v-btn>
      </div>
    </template>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn fab small color="green darken-2" dark @click="exportGroceries">
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="blue-grey" dark @click="printGroceries">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Refresh Button -->
      <v-btn fab small color="blue" dark @click="initialize">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import { defaultGrocery } from "@/models/food";
import GroceryDialog from "@/components/groceries/dialogs/GroceryDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import GlobalButtonExcel from "@/components/global/buttons/GlobalButtonExcel.vue";
import GlobalButtonPrint from "@/components/global/buttons/GlobalButtonPrint.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import groceryService from "@/services/grocery";

export default {
  mixins: [hasSnackbar, hasPrinting, hasExcel],
  components: {
    GroceryDialog,
    PageHeader,
    PageToolbar,
    MobileDataCards,
    GlobalButtonExcel,
    GlobalButtonPrint,
  },
  data: () => ({
    fabMenu: false,
    editGroceryDialog: false,
    cloneGroceryDialog: false,
    searchGrocery: "",
    searchParentGrocery: "",
    selectAllGroceries: false,
    groceries: [],
    editedGrocery: defaultGrocery,
    parentGroceries: [],
    isLoading: false,
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("id"), align: "id", value: "id" },
        { text: this.$t("code"), align: "start", value: "code" },
        { text: this.$t("title"), align: "start", value: "name" },
        {
          text: this.$t("q"),
          align: "start",
          value: "measure",
          sortable: false,
        },
        {
          text: this.$t("actions"),
          align: "center",
          value: "actions",
          sortable: false,
        },
        { text: "", value: "data-table-expand" },
      ];
    },
    headersN() {
      return [
        {
          text: this.$t("mark"),
          align: "center",
          value: "actions",
          sortable: false,
          mark: true,
        },
        { text: this.$t("title"), align: "start", value: "name" },
        {
          text: this.$t("q"),
          align: "start",
          value: "measure",
          sortable: false,
        },
      ];
    },
    prepData() {
      let items = {};
      this.groceries.filter((item) => {
        return items[item.clone_grocery_id] === item.clone_grocery_id;
      });
      return items;
    },
    mobileHeaders() {
      return [
        { text: this.$t("code"), value: "code", cols: 3 },
        { text: this.$t("title"), value: "name", cols: 4 },
        { text: this.$t("q"), value: "measure", cols: 2 },
        { text: this.$t("actions"), value: "actions", cols: 3 },
      ];
    },
  },
  watch: {
    editGroceryDialog(val) {
      val || this.close();
    },
    cloneGroceryDialog(val) {
      val || this.close();
    },
  },
  created() {
    if (this.$store.state.restaurant.parent_id) {
      this.getParentGroceries();
    }

    this.initialize();
  },
  methods: {
    saveClone() {
      let list = {};
      list["items"] = [];
      list["restoran"] = this.$store.state.restaurant.id;
      this.parentGroceries.filter((item) => {
        if (item.selected) {
          item.restoran = this.$store.state.restaurant.id;
          list["items"].push(item);
        }
      });
      groceryService.cloneParentGroceries(list).then(({ data }) => {
        if (data) {
          this.initialize();
          this.cloneGroceryDialog = false;
          this.searchParentGrocery = "";
        }
      });
    },
    selectAll() {
      if (this.selectAllGroceries) {
        this.parentGroceries.filter((item) => {
          item.selected = true;
        });
      } else {
        this.parentGroceries.filter((item) => {
          item.selected = false;
        });
      }
    },
    getParentGroceries() {
      groceryService
        .getParentGroceries(this.$store.state.restaurant.parent_id)
        .then(({ data }) => {
          data.filter((item) => {
            item.selected = false;
          });
          this.parentGroceries = data;
        });
    },
    async initialize() {
      this.isLoading = true;
      try {
        const res = await groceryService.getGroceries();
        this.groceries = res.data;
      } finally {
        this.isLoading = false;
      }
    },
    editItem(item) {
      this.editedGrocery = Object.assign({}, item);
      this.editGroceryDialog = true;
    },
    deleteItem(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("delete_grocery_text"), {
          color: "error",
        })
        .then(() => {
          groceryService.deleteGrocery(item.id).then(() => {
            if (item.parent_id) {
              const index = this.groceries.findIndex((i) => i.id === item.parent_id);
              const parentIndex = this.groceries[index].children.findIndex(
                (i) => (i.id = item.id)
              );
              this.groceries[index].children.splice(parentIndex, 1);
            } else {
              const index = this.groceries.indexOf(item);
              this.groceries.splice(index, 1);
            }
            this.showSuccessSnackbar(this.$t("ingredient_deleted"));
          });
        })
        .catch((err) => console.warn(err));
    },
    close() {
      this.editGroceryDialog = false;
      this.$nextTick(() => {
        this.editedGrocery = Object.assign({}, defaultGrocery);
      });
    },
    addGrocery(grocery) {
      const groceryToAdd = { ...this.editedGrocery, id: grocery.id };
      if (this.editedGrocery.parent_id) {
        let i = this.groceries.findIndex((i) => i.id === this.editedGrocery.parent_id);
        this.groceries[i].children.push(groceryToAdd);
      } else {
        this.groceries.push(groceryToAdd);
      }
      this.close();
    },
    updateGrocery() {
      if (this.editedGrocery.parent_id) {
        const index = this.groceries.findIndex(
          (i) => i.id === this.editedGrocery.parent_id
        );
        const parentIndex = this.groceries[index].children.findIndex(
          (i) => (i.id = this.editedGrocery.id)
        );
        Object.assign(this.groceries[index].children[parentIndex], this.editedGrocery);
      } else {
        const index = this.groceries.findIndex((i) => i.id === this.editedGrocery.id);
        Object.assign(this.groceries[index], this.editedGrocery);
      }
      this.close();
    },
    printGroceries() {
      this.setPrinter();

      const headers = [
        { text: this.$t("id"), value: "id", print: true },
        { text: this.$t("code"), value: "code", print: true },
        { text: this.$t("title"), value: "name", print: true },
        { text: this.$t("q"), value: "measure", print: true },
      ];

      const items = this.customSearch(this.groceries, this.headers, this.searchGrocery);

      this.printDocument({
        pageTitle: this.$t("grocery_list"),
        title: this.$t("grocery_list"),
        subtitle: this.$t("manage_ingredients"),
        tables: [
          {
            headers,
            items,
          },
        ],
      });
    },
    exportGroceries() {
      this.setExcel();

      const headers = [
        { text: this.$t("id"), value: "id", print: true },
        { text: this.$t("code"), value: "code", print: true },
        { text: this.$t("title"), value: "name", print: true },
        { text: this.$t("q"), value: "measure", print: true },
      ];

      const items = this.customSearch(this.groceries, this.headers, this.searchGrocery);
      const fileName = `namirnice_${new Date().toISOString().substr(0, 10)}.xlsx`;

      this.downloadExcel({
        fileName,
        sheets: [
          {
            name: this.$t("grocery_list") || "Namirnice",
            title: this.$t("grocery_list") || "Lista namirnica",
            subtitle:
              this.$t("manage_ingredients") || "Upravljajte receptima i namirnicama",
            headers,
            items,
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
